import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// Supported languages - same as in Translator
const LANGUAGES = [
  { code: "he-IL", name: "עברית", flag: "🇮🇱", shortCode: "he" },
  { code: "en-US", name: "English", flag: "🇺🇸", shortCode: "en" },
  { code: "ar-SA", name: "العربية", flag: "🇸🇦", shortCode: "ar" },
  { code: "ru-RU", name: "Русский", flag: "🇷🇺", shortCode: "ru" },
  { code: "fr-FR", name: "Français", flag: "🇫🇷", shortCode: "fr" },
  { code: "es-ES", name: "Español", flag: "🇪🇸", shortCode: "es" },
];

export default function TranslatorLanguagePicker() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // Get params from URL
  const type = searchParams.get("type") || "target"; // "source" or "target"
  const currentCode = searchParams.get("current") || "";
  const excludeCode = searchParams.get("exclude") || "";
  
  const title = type === "source" ? `${t("speaking")}:` : `${t("translation")}:`;
  const availableLanguages = useMemo(() => 
    LANGUAGES.filter((l) => l.code !== excludeCode),
    [excludeCode]
  );
  
  const handleSelect = useCallback((code: string) => {
    // Navigate back to translator with selected language
    navigate(`/translator?${type}Lang=${code}`, { replace: true });
  }, [navigate, type]);
  
  const handleCancel = useCallback(() => {
    navigate("/translator", { replace: true });
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>{title} - My EyeControl</title>
        <meta name="description" content="בחר שפה לתרגום" />
      </Helmet>

      <MainLayout onBack={handleCancel}>
        <div className="flex-1 flex flex-col overflow-hidden p-6" dir="rtl">
          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground text-center mb-8">{title}</h1>

          {/* Language Grid */}
          <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto w-full">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                data-scan-item
                onClick={() => handleSelect(lang.code)}
                className={cn(
                  "flex items-center gap-3 p-5 rounded-2xl border-2 transition-all",
                  currentCode === lang.code
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-foreground border-border hover:border-primary",
                  "active:scale-95"
                )}
              >
                <span className="text-3xl">{lang.flag}</span>
                <span className="text-lg font-medium">{lang.name}</span>
              </button>
            ))}
          </div>

          {/* Cancel Button */}
          <button
            data-scan-item
            onClick={handleCancel}
            className="mt-8 py-4 px-8 rounded-xl text-muted-foreground hover:bg-muted transition-all max-w-lg mx-auto w-full"
          >
            {t("cancel")}
          </button>
        </div>
      </MainLayout>
    </>
  );
}
